import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { ref } from 'vue'
import { ToastInterface, useToast } from 'vue-toastification'

import { Comment } from '@/interfaces/comment'
import { PaginatedResponse } from '@/interfaces/paginatedResponse'

interface Props {
	immediate?: boolean
	params: Params
}

interface Params {
	id: number
}

interface CreateParams extends Params {
	comment: string
}

const getHotelComments = async (
	params: Params
): Promise<PaginatedResponse<Comment>> => {
	const { data } = await axios.get<PaginatedResponse<Comment>>(
		`/api/internal/hotels/${params.id}/comments`
	)
	return data
}

const createHotelComment = async (
	params: CreateParams,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.post(
			`/api/internal/hotels/${params.id}/comments`,
			{
				commentary: params.comment,
			}
		)

		toast.success('Comentario creado correctamente')
		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const useHotelComments = (props: Props) => {
	const { immediate = true, params } = props
	const toast = useToast()

	const hotelId = ref(0)

	const { data, isLoading, refetch, isFetching, isRefetching } = useQuery(
		['hotelComments', params.id || hotelId.value],
		() =>
			getHotelComments({
				id: hotelId.value || props.params.id,
			}),
		{
			enabled: immediate,
		}
	)

	const setHotelId = (id: number) => {
		hotelId.value = id
	}

	const createCommentMutation = useMutation((params: CreateParams) =>
		createHotelComment(
			{
				id: params.id,
				comment: params.comment,
			},
			toast
		)
	)

	return {
		data,
		isLoading,
		setHotelId,
		hotelId,
		refetch,
		isFetching,
		isRefetching,

		createCommentMutation,
		createCommentMutationLoading: createCommentMutation.isLoading,
		createCommentMutationAsync: createCommentMutation.mutateAsync,
	}
}

export default useHotelComments
